import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, MessageSquare, Send, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SupportDraft } from "@/components/SupportDraft";
import { SITE } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back to you shortly.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact"
        description="Get in touch with the Kinect Nova AI team. We're here to help with any questions about the app, your account or partnerships."
        path="/contact"
      />
      <PageHeader
        eyebrow="Contact"
        title="We'd love to hear from you"
        subtitle="Questions, feedback or partnership ideas — reach out and our team will get back to you."
      />

      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-5 lg:gap-16">
          <Reveal className="lg:col-span-2">
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-foreground">
              Get in touch
            </h2>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              Our support team typically responds within one business day.
            </p>
            <div className="mt-8 space-y-5">
              <a
                href={`mailto:${SITE.supportEmail}`}
                data-testid="contact-email-link"
                className="flex items-start gap-4 rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">Email support</p>
                  <p className="text-sm text-muted-foreground">{SITE.supportEmail}</p>
                </div>
              </a>
              <div className="flex items-start gap-4 rounded-2xl border border-border bg-card p-5">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <MessageSquare className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">Help Center</p>
                  <p className="text-sm text-muted-foreground">Browse guides & FAQs at /help</p>
                </div>
              </div>
              <div className="flex items-start gap-4 rounded-2xl border border-border bg-card p-5">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">Website</p>
                  <p className="text-sm text-muted-foreground">{SITE.domain}</p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-3">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-3xl border border-border bg-card p-7 sm:p-9"
            >
              <SupportDraft
                onApply={({ subject, message }) =>
                  setForm((f) => ({ ...f, subject, message }))
                }
              />
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium">Name</Label>
                  <Input
                    id="name"
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Doe"
                    className="mt-2 rounded-xl"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-email-input"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="jane@example.com"
                    className="mt-2 rounded-xl"
                  />
                </div>
              </div>
              <div className="mt-5">
                <Label htmlFor="subject" className="text-sm font-medium">Subject</Label>
                <Input
                  id="subject"
                  data-testid="contact-subject-input"
                  value={form.subject}
                  onChange={update("subject")}
                  placeholder="How can we help?"
                  className="mt-2 rounded-xl"
                />
              </div>
              <div className="mt-5">
                <Label htmlFor="message" className="text-sm font-medium">Message</Label>
                <Textarea
                  id="message"
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us a little more..."
                  rows={6}
                  className="mt-2 rounded-xl"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground transition-all hover:opacity-90 disabled:opacity-60 sm:w-auto"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {loading ? "Sending..." : "Send message"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
